import React, { createContext, useContext, useState, ReactNode } from "react";
import { Transaction } from "@/types/transaction.type";

export type TransactionModalMode = "create" | "edit" | "delete";

export interface TransactionModalState {
  open: boolean;
  mode: TransactionModalMode;
  transaction?: Transaction;
}

export interface TransactionModalContextType {
  showCreate: () => void;
  showEdit: (transaction: Transaction) => void;
  showDelete: (transaction: Transaction) => void;
  hide: () => void;
  state: TransactionModalState;
}

const TransactionModalContext = createContext<
  TransactionModalContextType | undefined
>(undefined);

export const useTransactionModal = () => {
  const ctx = useContext(TransactionModalContext);
  if (!ctx)
    throw new Error(
      "useTransactionModal must be used within TransactionModalProvider"
    );
  return ctx;
};

export const TransactionModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, setState] = useState<TransactionModalState>({
    open: false,
    mode: "create",
  });

  const showCreate = () => setState({ open: true, mode: "create" });
  const showEdit = (transaction: Transaction) =>
    setState({ open: true, mode: "edit", transaction });
  const showDelete = (transaction: Transaction) =>
    setState({ open: true, mode: "delete", transaction });
  const hide = () => setState({ open: false, mode: "create" });

  return (
    <TransactionModalContext.Provider
      value={{
        showCreate,
        showEdit,
        showDelete,
        hide,
        state,
      }}
    >
      {children}
    </TransactionModalContext.Provider>
  );
};
